/**
 * Refresh-banner store — a tiny external store used by the global "your
 * data is stale" banner (spec §16 — "Last-write-wins with refresh banner
 * on row_version mismatch").
 *
 * Backing model: a boolean + a listener Set. Components read the flag via
 * `useSyncExternalStore(subscribe, getSnapshot)`. The store mutators
 * (`raise` / `dismiss`) are pure functions any caller can invoke — the
 * QueryClient event subscriber in `attach-refresh-banner.ts` calls
 * `raise()` whenever a mutation or query returns a row-version conflict.
 *
 * Why a module-level store rather than React Context: this signal is
 * truly global (the user's view of the app, not any single subtree) and
 * the conflict origin is the QueryClient cache, which lives outside the
 * React tree's render lifecycle. An external store hooked via
 * `useSyncExternalStore` is the canonical React 18+ pattern for exactly
 * this case.
 */

type Listener = () => void;

let conflictActive = false;
const listeners: Set<Listener> = new Set();

function notify(): void {
  for (const l of listeners) l();
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getSnapshot(): boolean {
  return conflictActive;
}

/**
 * Mark the banner active. Idempotent — calling raise() when already
 * active doesn't re-notify (avoids spurious re-renders on every
 * subsequent 409 during the same stale session).
 */
export function raise(): void {
  if (conflictActive) return;
  conflictActive = true;
  notify();
}

/**
 * Clear the banner. Called by the Dismiss button or by code that's
 * about to reload the page (so the banner doesn't flash before the
 * navigation completes).
 */
export function dismiss(): void {
  if (!conflictActive) return;
  conflictActive = false;
  notify();
}

/** Test-only — clear listeners + reset the flag between cases. */
export function __reset(): void {
  conflictActive = false;
  listeners.clear();
}
